import { isAndroid } from 'react-device-detect';

import { nextApiServiceInstance } from '~/domains/services/nextApi.instance';

import { getAnonymousId, getFirstTouchUTM, getLastTouchUTM, getContestIdFromUrl } from '../utils';
import { DeepLinkType } from '../deepLinks/types';


import type {
  AppsFlyerDataPayload,
  AppsFlyerDeepLinkPayload,
  APIAppsFlyerLinkResponse,
  AxiosAppsFlyerLinkResponse,
} from './types';


const getOnelinkId = () => {
  const env = process.env.NEXT_PUBLIC_ENV;

  switch (env) {
    case 'localhost':
    case 'development':
      return '2qJY';
    case 'staging':
      return 'j8Ql';
    // Poduction
    default:
      return '6OOI';
  }
}

const getBrandDomain = () => {
  const env = process.env.NEXT_PUBLIC_ENV;

  switch (env) {
    case 'localhost':
    case 'development':
      return 'link-dev.splashsports.com';
    case 'staging':
      return 'link-staging.splashsports.com';
    // Poduction
    default:
      return 'link.splashsports.com';
  }
}

const getAppId = () => {
  const env = process.env.NEXT_PUBLIC_ENV;

  switch (env) {
    case 'localhost':
    case 'development':
      if (isAndroid) return 'com.splash.splashsports.develop';
      return 'id6499585246';
    case 'staging':
      if (isAndroid) return 'com.splash.splashsports.staging';
      return 'id6499584875';
    // Poduction
    default:
      if (isAndroid) return 'com.splash.splashsports';
      return 'id6451190245';
  }
}

const constructAppsFlyerDeepLinkData = (
  deepLink: string,
  deepLinkType: DeepLinkType
): AppsFlyerDataPayload => {
  const host =
    process.env.NEXT_PUBLIC_ENV === 'localhost'
      ? 'http://dev.splashsports.com'
      : window.location.origin;
  const fullUrl = `${host}${deepLink}`;
  const referralRegexp = /[&?]referralCode=([^&]*)/;
  const referralCode =
    deepLink.match(referralRegexp)?.[1] || window.location.search.match(referralRegexp)?.[1];
  const contestId = getContestIdFromUrl(deepLink);

  // Base data payload with common parameters
  const baseData: AppsFlyerDataPayload = {
    pid: 'splash-web',
    af_adset: 'web-dynamic-links',
    deep_link_value: fullUrl,
    deep_link_sub1: getAnonymousId(), // Use sub1 for anonymous ID tracking
  };

  switch (deepLinkType) {
    case DeepLinkType.CONTEST_DETAIL:
      return {
        ...baseData,
        c: 'contest-detail',
        af_og_image: contestId && `${host}/og?contestUuid=${contestId}&isDeadlineEnabled=false`,
        deep_link_sub2: contestId || undefined,
      };
    case DeepLinkType.UNSUPPORTED_FEATURE:
      return {
        ...baseData,
        c: 'unsupported-feature',
        af_og_image: contestId && `${host}/og?contestUuid=${contestId}&isDeadlineEnabled=false`,
        deep_link_sub2: contestId || undefined,
      };
    case DeepLinkType.REFER_A_FRIEND:
      return {
        ...baseData,
        c: 'refer-a-friend',
        af_og_image: referralCode && `${host}/og/type/referral?code=${referralCode}`,
        deep_link_sub2: referralCode || undefined,

        // RAF links should always have web redirects
        af_r: fullUrl, // Complete redirect override
      };
    case DeepLinkType.SIGN_UP:
      return {
        ...baseData,
        c: 'sign-up',
        af_og_image: referralCode && `${host}/og/type/referral?code=${referralCode}`,
        deep_link_sub2: referralCode || undefined,
      };

    default:
      // Default payload for unsupported features
      return baseData;
  }
};

const constructAppsFlyerDeepLinkPayload = (
  deepLink: string,
  deepLinkType: DeepLinkType,
): AppsFlyerDeepLinkPayload => {
  const data = constructAppsFlyerDeepLinkData(deepLink, deepLinkType);
  const firstTouchUtm = getFirstTouchUTM();
  const utmParams =
    firstTouchUtm && typeof firstTouchUtm === 'object' && Object.keys(firstTouchUtm).length > 0
      ? firstTouchUtm
      : getLastTouchUTM();

  // Override default AppsFlyer params when UTM campaign is detected
  if (utmParams) {
    for (const [key, value] of Object.entries(utmParams)) {
      switch (key) {
        case 'utm_source':
          data.pid = value;
          break;
        case 'utm_campaign':
          data.c = value;
          break;
        case 'utm_medium':
          data.af_adset = value;
          break;
        case 'utm_content':
          data.af_ad = value;
          break;
        case 'utm_channel':
          data.af_channel = value;
          break;
        case 'utm_term':
          data.af_keywords = value;
          break;
        default:
          break;
      }
    }
  }

  // Filter out undefined values
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  ) as Record<string, string>;

  return {
    ttl: '31d', // Default TTL of 31 days
    data: cleanData,
    brand_domain: getBrandDomain(),
  };
};


const fireAppsFlyerImpression = async (payload: AppsFlyerDeepLinkPayload['data']): Promise<void> => {
  const appId = getAppId();
  const impressionPayload = {
    pid: payload.pid,
    c: payload.c,
    af_adset: payload.af_adset,
    af_ad: payload.af_ad,
    af_channel: payload.af_channel,
    af_keywords: payload.af_keywords,
    af_sub1: payload.deep_link_sub1,
    af_sub2: payload.deep_link_sub2,
  }

  const url = `/impression/${appId}`;
  const response = await nextApiServiceInstance.get(
    url,
    { params: impressionPayload }
  );

  return response.data;
};

export const getAppsFlyerLink = async (
  deepLinkURL: string,
  deepLinkType: DeepLinkType,
  customId?: string
): Promise<APIAppsFlyerLinkResponse> => {
  const token = process.env.NEXT_PUBLIC_APPSFLYER_OPENLINK_API_TOKEN;
  const isAppsFlyerEnabled = !!token && token !== 'false';
  const payload = constructAppsFlyerDeepLinkPayload(deepLinkURL, deepLinkType);
  const onelinkId = getOnelinkId();

  // Revert to long link if AppsFlyer is disabled
  if (!isAppsFlyerEnabled) {
    const url = new URL(`https://link.splashsports.com/${onelinkId}/download`);
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(payload.data)) {
      searchParams.set(key, value as string);
    };

    url.search = searchParams.toString();

    return url.toString();
  }

  // Construct the URL with onelink-id and optional custom id
  const url = customId ? `/deeplink/${onelinkId}?id=${customId}` : `/deeplink/${onelinkId}`;
  const response = await nextApiServiceInstance.post<AxiosAppsFlyerLinkResponse>(
    url,
    payload
  );

  // Fire AppsFlyer impression
  await fireAppsFlyerImpression(payload.data);

  // @ts-expect-error FIXME: No idea why type inference doesn't work here
  return response.data;
};
